import styled from '@emotion/styled';
import React, { FC, useMemo, useState } from 'react';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import { Box, Button, GreenButton, WhiteButton } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import PercentDiscountIcon from '~Assets/icons/percent_discount.svg';
import MessageInput from '~ComponentsImproved/MessageInput';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import useOffers from '~Hooks/useOffers';
import { useCart } from '~Components/CartProvider';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import useBongQuery from '~Scenes/POS/Queries/query';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { GetOffers_Company_allPromoCodes } from '~Hooks/__queries__';
import { DISCOUNT_TYPE, SERVICE_TYPE } from '~../__queries__';

const DiscountButton = styled(WhiteButton)`
  background-color: var(--colorPOSClassic16);
`;

const DiscountBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIGreen) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIGreen) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
`;

const CenteredBox = styled(Box)`
  text-align: center;
`;

/** How many limited categories to list by name before collapsing the rest into "+X more" */
const MAX_LISTED_CATEGORIES = 5;

interface Props {
  isQSR?: boolean;
  onClick?: () => void;
  /** Called instead of the default cart.setAppliedDiscount when the promo code is confirmed */
  onProceed?: (promoCode: GetOffers_Company_allPromoCodes) => void;
}

const PromoCodeDiscount: FC<Props> = ({ isQSR, onClick, onProceed }) => {
  const venueId = useSelector((state) => state.venueId) as string;
  const isHandy = window.innerWidth <= 600;

  const modal = useModal();
  const offers = useOffers();
  const cart = useCart();
  const bongQuery = useBongQuery(venueId);
  const currency = useCurrency();
  const { i18n } = useI18n();

  const [promoCode, setPromoCode] = useState<GetOffers_Company_allPromoCodes | null>(null);
  const [error, setError] = useState<string>('');

  const promoCodeButton = useButton({
    onClick: () => {
      setError('');
      setPromoCode(null);

      if (onClick) {
        onClick();
      }

      modal.show();
    },
  });

  const handleSubmit = (code: string) => {
    const result = offers.findByPromoCode(code, isQSR ? cart.serviceType : SERVICE_TYPE.EAT_HERE);

    if (result.success && result.promoCode) {
      setPromoCode(result.promoCode);
      return;
    }

    setError(result.errorMessage || '');
  };

  const categoryNames = useMemo(() => {
    const categories = bongQuery.data?.company?.categories || [];

    return (promoCode?.categoryIds || []).map((categoryId) => {
      const category = categories.find((c) => c.id === categoryId);

      return category ? i18n(category.nameLang) : categoryId;
    });
  }, [bongQuery.data, promoCode, i18n]);

  // Items belonging to the promo code's limited categories, same as itemIdsForOffer in Discounts.tsx
  const itemIds = useMemo(() => {
    const categories = bongQuery.data?.company?.categories || [];

    return (promoCode?.categoryIds || []).flatMap(
      (categoryId) => categories.find((c) => c.id === categoryId)?.items.map((i) => i.id) ?? [],
    );
  }, [bongQuery.data, promoCode]);

  const discountText = promoCode
    ? promoCode.discountType === DISCOUNT_TYPE.PERCENTAGE
      ? `${promoCode.discountRate}%`
      : currency.format(promoCode.discountRate)
    : '';

  const cancelButton = useButton({
    onClick: () => {
      modal.hide();
    },
  });

  const proceedButton = useButton({
    onClick: () => {
      if (!promoCode) {
        return;
      }

      if (onProceed) {
        onProceed(promoCode);
      } else {
        cart.setAppliedDiscount({
          id: promoCode.id,
          type: promoCode.discountType,
          value: promoCode.discountRate,
          itemIds,
          title: i18n(promoCode.title),
        });
      }

      modal.hide();
    },
  });

  return (
    <>
      <DiscountButton fluid {...promoCodeButton}>
        <Box display="flex" alignItems="center" justifyContent="center" gap="xs">
          <Icon
            height="24px"
            width="24px"
            src={PercentDiscountIcon}
            color="var(--colorBlueGray1200)"
          />
          {i18n('PromoCode.ButtonLabel')}
        </Box>
      </DiscountButton>

      <Modal isShowing={modal.isShowing} hide={modal.hide}>
        {promoCode ? (
          <ModalWrapper
            padding={isHandy ? 'large' : '3xl'}
            width={isHandy ? '340px' : '480px'}
            gap="small"
            header={{
              icon: PercentDiscountIcon,
              title: i18n(promoCode.title),
              subtitle: i18n('PromoCode.Applied'),
            }}>
            <DiscountBox fluid display="flex" alignItems="center" justifyContent="center">
              <Paragraph level={4} color="var(--colorUIGreen)">
                {i18n('PromoCode.Discount', discountText)}
              </Paragraph>
            </DiscountBox>

            {categoryNames.length > 0 && (
              <CenteredBox>
                <Paragraph
                  style={{ fontStyle: 'italic' }}
                  level={5}
                  color="var(--colorBlueGray200)">
                  {i18n(
                    'PromoCode.LimitedTo',
                    categoryNames.slice(0, MAX_LISTED_CATEGORIES).join(', '),
                  )}
                  {categoryNames.length > MAX_LISTED_CATEGORIES &&
                    i18n('PromoCode.AndMore', categoryNames.length - MAX_LISTED_CATEGORIES)}
                </Paragraph>
              </CenteredBox>
            )}

            <Box fluid display="flex" gap="xs" marginTop="small">
              <Button fluid {...cancelButton}>
                {i18n('General.Cancel')}
              </Button>
              <GreenButton fluid {...proceedButton}>
                {i18n('PromoCode.Apply')}
              </GreenButton>
            </Box>
          </ModalWrapper>
        ) : (
          <MessageInput
            type="text"
            placeHolder={i18n('PromoCode.EnterCode')}
            onSubmit={handleSubmit}
            error={error}
          />
        )}
      </Modal>
    </>
  );
};

export default React.memo(PromoCodeDiscount);
